import React, { useState, useContext, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Card, Grid } from '@mui/material'
import { AnyInput, useValidatedForm } from '../anyform'
import {
    createKeyPair,
    getKeyPair,
    updateKeyPair,
    metadata,
    emptyPromise,
} from '../api'
import { FormActions, Retrievable, Surface, KeyPairsIcon } from '../components'
import { AppContext } from '../context'
import { FeedbackContext } from '../feedback'
import { Griddable } from '../grid'
import { translate } from '../i18n'
import { KeyPair, KeyPairRequest, KeyPairMetadataResponse } from '../model'
import { RouterParams } from '../router'

type Data = [KeyPair | undefined, KeyPairMetadataResponse]

export default function KeyPairForm({ match }: RouterParams) {
    const history = useHistory()
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)

    const tenantId = context.session?.tenant?.id ?? '-'
    const keyPairId = match.params.keyPairId
    const [keyPair, setKeyPair] = useState<KeyPair>()
    const [actions, setActions] = useState<string[]>([])
    const [selected, setSelected] = useState<string[]>([])
    const { register, bind, isValid } = useValidatedForm<KeyPairRequest>()

    const retrievePromise = useCallback(() => {
        if (keyPairId) {
            return getKeyPair(tenantId, keyPairId)
        }
        return emptyPromise()
    }, [tenantId, keyPairId])

    const metadataPromise = useCallback(() => {
        return metadata(tenantId)
    }, [tenantId])

    const fetchData = useCallback(async (): Promise<Data> => {
        const data = await Promise.all([retrievePromise(), metadataPromise()])

        return data
    }, [retrievePromise, metadataPromise])

    const setData = useCallback(([keyPair, metadata]: Data) => {
        setKeyPair(keyPair)
        setSelected(keyPair?.actions ?? [])
        setActions(metadata.actions)
    }, [])

    const submitPromise = (): Promise<KeyPair> => {
        const request = { ...bind(), actions: selected }

        if (keyPairId) {
            return updateKeyPair(tenantId, keyPairId, request)
        }
        return createKeyPair(tenantId, request)
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()
        submitPromise()
            .then((keyPair) => {
                feedbackContext.showSuccess(
                    translate(`keypairs.${keyPairId ? 'edited' : 'created'}`, {
                        name: keyPair.name,
                    }) as string
                )
                history.push('/keypairs')
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }

    return (
        <Retrievable
            getPromise={fetchData}
            setData={setData}
            builder={() => (
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={10} lg={7} xl={5}>
                            <Surface
                                title={translate(
                                    keyPairId ? 'keypairs.edit' : 'keypairs.new'
                                )}
                                icon={<KeyPairsIcon />}
                            >
                                <form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={onSubmit}
                                >
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Grid item xs={12}>
                                            <AnyInput
                                                type="text"
                                                name="name"
                                                initialValue={keyPair?.name}
                                                label={
                                                    translate(
                                                        'keypairs.name'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Card variant="outlined">
                                                <Griddable
                                                    items={actions}
                                                    getId={(action, index) =>
                                                        `${action}`
                                                    }
                                                    loading={false}
                                                    empty={
                                                        translate(
                                                            'keypairs.empty_actions'
                                                        ) as string
                                                    }
                                                    selectable={{
                                                        selected,
                                                        onChange(ids, items) {
                                                            setSelected(ids)
                                                        },
                                                    }}
                                                    columns={[
                                                        {
                                                            title: translate(
                                                                'keypairs.action'
                                                            ),
                                                            converter: (
                                                                action
                                                            ) => `${action}`,
                                                        },
                                                    ]}
                                                />
                                            </Card>
                                        </Grid>
                                        <FormActions
                                            id={keyPairId}
                                            disabled={selected.length === 0}
                                        />
                                    </Grid>
                                </form>
                            </Surface>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        />
    )
}
